import { useEffect, useRef, useState } from 'react';

import {
	Button,
	Card,
	Collapse,
	Flex,
	Select,
	Slider,
	Spin,
	Splitter,
	Tabs,
	Typography,
	message,
} from 'antd';
import {
	ArrowLeftOutlined,
	DownOutlined,
	LeftOutlined,
	MinusOutlined,
	PlusOutlined,
	RightOutlined,
	UpOutlined,
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useGetJawAssembliesQuery } from '../../../redux/api/versabuilt';
import {
	useLazyGetOrderLineItemsQuery,
} from '../../../redux/api/order-line-item';
import {
	useLazyGetCamDataJawsQuery,
	useLazyGetOrderLineItemByIdV2Query,
} from '../../../redux/api/order';
import { Lineitem } from '../../../interfaces/Ordel.Interface';
import { JawAssembly } from '../../../interfaces/VersaBuilt.Interface';
import { CamDataJawsRow } from '../../../interfaces/Fusion360SetupData.interfaces';
import {
	SelectableJawConfiguration,
	findConfigurationInJawAssemblies,
	findJawAssemblyForConfiguration,
	fitToAspectRatio,
	getAvailableStockLengths,
	getAvailableStockWidths,
	getConfigMinLength,
	getConfigStockWidth,
	getConfigurationFilters,
	getFilteredConfigurations,
	isRoundStockShape,
	normalizeBase64PngToDataUrl,
} from './helpers';

const { Title } = Typography;
const { Option } = Select;

// Query params kept compatible with the standalone jaw assistant so any
// bookmarked deep links keep working after the migration.
const CONFIGURATION_QUERY_PARAM = 'configuration';
const TAB_QUERY_PARAM = 'tab';
const PRODUCT_QUERY_PARAM = 'product';

const LINE_ITEM_SEARCH_STATUS = 'Received,In Progress';

// The layout, inline styles, and the video/overlay stack below are ported
// from the standalone app: the overlay positioning against the live camera
// feed is hand-tuned at the jaw assembly station (see PTB-6330). Deliberate
// deviations from the standalone (post-deploy station feedback): camera
// permission is requested before device enumeration, the camera picker shows
// whenever a camera exists, and the overlay is sized by feed height so 100%
// scale matches the video height.
const JawConfiguration: React.FC = () => {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();

	const videoRef = useRef<HTMLVideoElement>(null);
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const viewportRef = useRef<HTMLDivElement>(null);
	const panelRef = useRef<HTMLDivElement>(null);
	const hasRestoredConfigurationFromUrl = useRef(false);
	const hasRestoredProductFromUrl = useRef(false);

	// === Video, Overlay, and Misc States ===
	const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
	const [selectedDeviceId, setSelectedDeviceId] = useState('');
	const [selectedJawAssembly, setSelectedJawAssembly] =
		useState<JawAssembly | null>(null);
	const [selectedStockShape, setSelectedStockShape] = useState<string | null>(
		null
	);
	const [selectedStockWidth, setSelectedStockWidth] = useState<
		number | string | null
	>(null);
	const [selectedStockLength, setSelectedStockLength] = useState<
		number | string | null
	>(null);
	const [selectedConfiguration, setSelectedConfiguration] =
		useState<SelectableJawConfiguration | null>(null);
	const [overlayImage, setOverlayImage] = useState<string | null>(null);
	// Calibration values are per-station physical calibration; the
	// localStorage keys match the standalone app so nothing is lost on
	// upgrade of this page.
	const [overlaySize, setOverlaySize] = useState<number>(() => {
		const saved = localStorage.getItem('overlaySize');
		return saved !== null ? parseFloat(saved) : 100;
	});
	const [overlayOffsetX, setOverlayOffsetX] = useState<number>(() => {
		const saved = localStorage.getItem('overlayOffsetX');
		return saved !== null ? parseFloat(saved) : 0;
	});
	const [overlayOffsetY, setOverlayOffsetY] = useState<number>(() => {
		const saved = localStorage.getItem('overlayOffsetY');
		return saved !== null ? parseFloat(saved) : 0;
	});
	const [overlayOpacity, setOverlayOpacity] = useState(100);

	useEffect(() => {
		localStorage.setItem('overlaySize', overlaySize.toString());
	}, [overlaySize]);
	useEffect(() => {
		localStorage.setItem('overlayOffsetX', overlayOffsetX.toString());
	}, [overlayOffsetX]);
	useEffect(() => {
		localStorage.setItem('overlayOffsetY', overlayOffsetY.toString());
	}, [overlayOffsetY]);

	// === Order Search States ===
	const [orders, setOrders] = useState<Lineitem[]>([]);
	const [selectedOrderLineItem, setSelectedOrderLineItem] =
		useState<Lineitem | null>(null);
	// The selected line item's CAM exports, from the `cam-data/jaws/` slice.
	const [camJawExports, setCamJawExports] = useState<CamDataJawsRow[] | null>(
		null
	);
	const [isLoadingOrderLineItem, setIsLoadingOrderLineItem] = useState(false);
	const [pendingOrderLineItemId, setPendingOrderLineItemId] = useState<
		string | null
	>(null);
	const [searchTab, setSearchTab] = useState(() =>
		searchParams.get(TAB_QUERY_PARAM) === 'jaws' ? '2' : '1'
	);

	// === Lens Distortion States ===
	const [k1, setK1] = useState<number>(() => {
		const saved = localStorage.getItem('lensK1');
		return saved !== null ? parseFloat(saved) : 0.0;
	});
	const [k2, setK2] = useState<number>(() => {
		const saved = localStorage.getItem('lensK2');
		return saved !== null ? parseFloat(saved) : 0.0;
	});
	useEffect(() => {
		localStorage.setItem('lensK1', k1.toString());
	}, [k1]);
	useEffect(() => {
		localStorage.setItem('lensK2', k2.toString());
	}, [k2]);

	// Use a ref so that the WebGL render loop always has the latest values.
	const distortionRef = useRef({ k1, k2 });
	useEffect(() => {
		distortionRef.current = { k1, k2 };
	}, [k1, k2]);

	// === Video Aspect Ratio State ===
	const [videoAspectRatio, setVideoAspectRatio] = useState(16 / 9);
	const [viewportLayout, setViewportLayout] = useState<{
		width: number;
		height: number;
	} | null>(null);

	useEffect(() => {
		const panel = panelRef.current;
		if (!panel) return;

		const updateLayout = () => {
			setViewportLayout(
				fitToAspectRatio(panel.clientWidth, panel.clientHeight, videoAspectRatio)
			);
		};

		updateLayout();
		const observer = new ResizeObserver(updateLayout);
		observer.observe(panel);
		return () => observer.disconnect();
	}, [videoAspectRatio]);

	// === API ===
	const { data: jawAssemblies = [] } = useGetJawAssembliesQuery();
	const [fetchLineItems] = useLazyGetOrderLineItemsQuery();
	const [fetchLineItemById] = useLazyGetOrderLineItemByIdV2Query();
	const [fetchCamDataJaws] = useLazyGetCamDataJawsQuery();

	const updateUrlQueryParams = (params: Record<string, string | null>) => {
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev);
				Object.entries(params).forEach(([key, value]) => {
					if (value == null || value === '') {
						next.delete(key);
					} else {
						next.set(key, value);
					}
				});
				return next;
			},
			{ replace: true }
		);
	};

	const setConfigurationQueryParam = (configId: string | null) => {
		updateUrlQueryParams({ [CONFIGURATION_QUERY_PARAM]: configId });
	};

	useEffect(() => {
		if (hasRestoredConfigurationFromUrl.current || jawAssemblies.length === 0) {
			return;
		}

		const configId = searchParams.get(CONFIGURATION_QUERY_PARAM);
		if (!configId) {
			hasRestoredConfigurationFromUrl.current = true;
			return;
		}

		const match = findConfigurationInJawAssemblies(jawAssemblies, configId);
		if (match) {
			const { jawAssembly, config } = match;
			setSelectedJawAssembly(jawAssembly);
			setSelectedStockShape(config.stock_shape ?? null);
			setSelectedStockWidth(getConfigStockWidth(config));
			setSelectedStockLength(getConfigMinLength(config));
			setSelectedConfiguration(config);
			if (config.wireframe_overlay) {
				setOverlayImage(config.wireframe_overlay);
			}
		}

		hasRestoredConfigurationFromUrl.current = true;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [jawAssemblies]);

	// NOTE: successive setSearchParams calls in one tick clobber each other
	// (react-router's functional updater sees the same stale params), so every
	// user action must funnel into a SINGLE updateUrlQueryParams call —
	// extraParams lets callers piggyback tab/product changes onto it.
	const applyConfigurationSelection = (
		config: SelectableJawConfiguration | null | undefined,
		extraParams: Record<string, string | null> = {}
	) => {
		if (!config) {
			setSelectedConfiguration(null);
			setOverlayImage(null);
			updateUrlQueryParams({
				[CONFIGURATION_QUERY_PARAM]: null,
				...extraParams,
			});
			return;
		}

		const jawAssembly = findJawAssemblyForConfiguration(jawAssemblies, config);
		if (jawAssembly) {
			setSelectedJawAssembly(jawAssembly);
		}

		setSelectedStockShape(config.stock_shape ?? null);
		setSelectedStockWidth(getConfigStockWidth(config));
		setSelectedStockLength(getConfigMinLength(config));
		setSelectedConfiguration(config);
		setOverlayImage(config.wireframe_overlay ?? null);
		updateUrlQueryParams({
			[CONFIGURATION_QUERY_PARAM]: config.id,
			...extraParams,
		});
	};

	const clearJawConfigurationSelection = () => {
		setSelectedStockShape(null);
		setSelectedStockWidth(null);
		setSelectedStockLength(null);
		setSelectedConfiguration(null);
		setOverlayImage(null);
		setConfigurationQueryParam(null);
	};

	const clearJawsTabSelection = () => {
		setSelectedJawAssembly(null);
		clearJawConfigurationSelection();
	};

	const clearProductSelectionState = () => {
		setSelectedOrderLineItem(null);
		setCamJawExports(null);
		setPendingOrderLineItemId(null);
		setOverlayImage(null);
	};

	const clearProductSelection = () => {
		clearProductSelectionState();
		updateUrlQueryParams({ [PRODUCT_QUERY_PARAM]: null });
	};

	// === Video Devices ===
	useEffect(() => {
		// getUserMedia/enumerateDevices only exist in a secure context
		// (https or localhost). Over plain http on a LAN host they are
		// undefined — bail out cleanly so the rest of the page still works.
		const mediaDevices = navigator.mediaDevices;
		if (!mediaDevices?.enumerateDevices) {
			console.error(
				'navigator.mediaDevices unavailable — the camera needs a secure ' +
					'context (https or localhost). Camera picker disabled.'
			);
			return;
		}
		let cancelled = false;
		const fetchDevices = async () => {
			try {
				const allDevices = await mediaDevices.enumerateDevices();
				const videoDevices = allDevices.filter(
					(device) => device.kind === 'videoinput'
				);
				if (cancelled) return videoDevices;
				setDevices(videoDevices);
				setSelectedDeviceId((prev) =>
					prev && videoDevices.some((device) => device.deviceId === prev)
						? prev
						: videoDevices[0]?.deviceId ?? ''
				);
				return videoDevices;
			} catch (error) {
				console.error('Error fetching devices: ', error);
				return [];
			}
		};
		const init = async () => {
			// Enumerate first so the picker shows immediately — a real camera's
			// getUserMedia stays pending until the user answers the permission
			// prompt, and awaiting it first would hide the picker until then.
			const videoDevices = await fetchDevices();
			// Real (non-empty) deviceIds mean the origin already holds a camera
			// grant: the display stream request will succeed on its own, and it
			// fires the permission prompt itself in browsers that expose ids
			// pre-grant. Probing anyway opens the camera before the HD display
			// request, which then settles into the probe's capture mode — the
			// feed came up low-res on every visit after the first.
			if (videoDevices.some((device) => device.deviceId)) return;
			try {
				// Placeholder entries only (empty deviceIds): no stream request
				// will ever fire, so probe to make the browser show the prompt.
				// Ask for HD so a concurrent display request can't get pinned to
				// the camera's low-res default mode.
				const probe = await mediaDevices.getUserMedia({
					video: { width: { ideal: 1920 }, height: { ideal: 1080 } },
				});
				probe.getTracks().forEach((track) => track.stop());
			} catch (error) {
				console.error('Error requesting camera access: ', error);
			}
			// Re-enumerate: labels/deviceIds are only populated after the grant.
			await fetchDevices();
		};
		init();
		const handleDeviceChange = () => {
			fetchDevices();
		};
		mediaDevices.addEventListener?.('devicechange', handleDeviceChange);
		return () => {
			cancelled = true;
			mediaDevices.removeEventListener?.('devicechange', handleDeviceChange);
		};
	}, []);

	// === Get Video Stream ===
	useEffect(() => {
		const getCameraStream = async (deviceId: string) => {
			const constraints: MediaStreamConstraints = {
				video: {
					deviceId: { exact: deviceId },
					width: { ideal: 1920 },
					height: { ideal: 1080 },
				},
				audio: false,
			};
			try {
				const stream = await navigator.mediaDevices.getUserMedia(constraints);
				if (videoRef.current) {
					videoRef.current.srcObject = stream;
					videoRef.current.onloadedmetadata = () => {
						if (!videoRef.current) return;
						setVideoAspectRatio(
							videoRef.current.videoWidth / videoRef.current.videoHeight
						);
					};
				}
			} catch (error) {
				console.error('Error accessing the camera: ', error);
			}
		};
		if (selectedDeviceId) {
			getCameraStream(selectedDeviceId);
		}
		const video = videoRef.current;
		return () => {
			if (video && video.srcObject) {
				const stream = video.srcObject as MediaStream;
				stream.getTracks().forEach((track) => track.stop());
			}
		};
	}, [selectedDeviceId]);

	// === WebGL Setup for Lens Distortion Correction ===
	useEffect(() => {
		const video = videoRef.current;
		const canvas = canvasRef.current;
		if (!video || !canvas) return;
		const gl = canvas.getContext('webgl');
		if (!gl) {
			console.error('WebGL not supported');
			return;
		}

		// Vertex Shader
		const vertexShaderSource = `
			attribute vec2 a_position;
			attribute vec2 a_texCoord;
			varying vec2 v_texCoord;
			void main() {
				gl_Position = vec4(a_position, 0, 1);
				v_texCoord = a_texCoord;
			}
		`;

		// Fragment Shader: Apply lens distortion correction only.
		const fragmentShaderSource = `
			precision mediump float;
			uniform sampler2D u_texture;
			uniform float u_k1;
			uniform float u_k2;
			varying vec2 v_texCoord;
			void main(){
				vec2 centered = v_texCoord - vec2(0.5, 0.5);
				float r2 = dot(centered, centered);
				float factor = 1.0 + u_k1 * r2 + u_k2 * r2 * r2;
				vec2 corrected = centered * factor + vec2(0.5, 0.5);
				gl_FragColor = texture2D(u_texture, corrected);
			}
		`;

		const compileShader = (source: string, type: number) => {
			const shader = gl.createShader(type);
			if (!shader) return null;
			gl.shaderSource(shader, source);
			gl.compileShader(shader);
			if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
				console.error('Shader compile failed:', gl.getShaderInfoLog(shader));
				gl.deleteShader(shader);
				return null;
			}
			return shader;
		};

		const vertexShader = compileShader(vertexShaderSource, gl.VERTEX_SHADER);
		const fragmentShader = compileShader(
			fragmentShaderSource,
			gl.FRAGMENT_SHADER
		);
		if (!vertexShader || !fragmentShader) return;

		const program = gl.createProgram();
		if (!program) return;
		gl.attachShader(program, vertexShader);
		gl.attachShader(program, fragmentShader);
		gl.linkProgram(program);
		if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
			console.error('Program failed to link:', gl.getProgramInfoLog(program));
			return;
		}
		gl.useProgram(program);

		// Set up vertices for a rectangle (two triangles)
		const vertices = new Float32Array([
			-1, -1, 0, 0, 1, -1, 1, 0, -1, 1, 0, 1, 1, 1, 1, 1,
		]);
		const vertexBuffer = gl.createBuffer();
		gl.bindBuffer(gl.ARRAY_BUFFER, vertexBuffer);
		gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);
		const FSIZE = vertices.BYTES_PER_ELEMENT;
		const a_position = gl.getAttribLocation(program, 'a_position');
		gl.vertexAttribPointer(a_position, 2, gl.FLOAT, false, FSIZE * 4, 0);
		gl.enableVertexAttribArray(a_position);
		const a_texCoord = gl.getAttribLocation(program, 'a_texCoord');
		gl.vertexAttribPointer(a_texCoord, 2, gl.FLOAT, false, FSIZE * 4, FSIZE * 2);
		gl.enableVertexAttribArray(a_texCoord);

		// Create texture
		const texture = gl.createTexture();
		gl.bindTexture(gl.TEXTURE_2D, texture);
		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
		const u_texture = gl.getUniformLocation(program, 'u_texture');
		gl.uniform1i(u_texture, 0);
		const u_k1 = gl.getUniformLocation(program, 'u_k1');
		const u_k2 = gl.getUniformLocation(program, 'u_k2');

		// Size the canvas buffer to match the shared viewport box.
		const updateCanvasSize = () => {
			const viewport = viewportRef.current;
			if (!viewport) return;
			const displayWidth = viewport.clientWidth;
			const displayHeight = viewport.clientHeight;
			if (
				displayWidth > 0 &&
				displayHeight > 0 &&
				(canvas.width !== displayWidth || canvas.height !== displayHeight)
			) {
				canvas.width = displayWidth;
				canvas.height = displayHeight;
				gl.viewport(0, 0, displayWidth, displayHeight);
			}
		};

		let animationFrameId: number;
		const render = () => {
			updateCanvasSize();
			if (video.readyState >= video.HAVE_CURRENT_DATA) {
				gl.bindTexture(gl.TEXTURE_2D, texture);
				gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, video);
				gl.uniform1f(u_k1, distortionRef.current.k1);
				gl.uniform1f(u_k2, distortionRef.current.k2);
				gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
			}
			animationFrameId = requestAnimationFrame(render);
		};
		render();
		return () => cancelAnimationFrame(animationFrameId);
	}, [videoAspectRatio]);

	// === Order Search Functions ===
	const fetchOrders = async (search: string) => {
		try {
			const data = await fetchLineItems({
				search,
				status: LINE_ITEM_SEARCH_STATUS,
			}).unwrap();
			setOrders(data.results || []);
		} catch (error) {
			message.error(`Error fetching orders: ${error}`);
		}
	};

	useEffect(() => {
		fetchOrders('');
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Two light requests instead of the v1 line-item detail, whose
	// `fusion_360_cam_data_exports` carries the raw CAM blob (10–40 MB per
	// export in prod — what made the station feel slow). The line item itself
	// is only needed for the dropdown label, so it is skipped when the search
	// results already have it.
	const loadOrderLineItemById = async (
		itemId: string,
		{ updateUrl = true } = {}
	) => {
		setIsLoadingOrderLineItem(true);
		setPendingOrderLineItemId(itemId);
		try {
			const knownItem = orders.find((order) => order.id === itemId);
			const [itemData, camExports] = await Promise.all([
				knownItem ?? fetchLineItemById({ id: itemId }).unwrap(),
				fetchCamDataJaws({ id: itemId }).unwrap(),
			]);
			setSelectedOrderLineItem(itemData);
			setCamJawExports(camExports);
			setOrders((prev) => {
				if (prev.some((order) => order.id === itemData.id)) {
					return prev;
				}
				return [itemData, ...prev];
			});
			if (updateUrl) {
				updateUrlQueryParams({
					[PRODUCT_QUERY_PARAM]: itemId,
					[TAB_QUERY_PARAM]: 'product',
				});
			}
		} catch (error) {
			message.error(`Error fetching order line item details: ${error}`);
		} finally {
			setIsLoadingOrderLineItem(false);
			setPendingOrderLineItemId(null);
		}
	};

	useEffect(() => {
		if (hasRestoredProductFromUrl.current) {
			return;
		}

		hasRestoredProductFromUrl.current = true;
		const productId = searchParams.get(PRODUCT_QUERY_PARAM);
		if (productId) {
			loadOrderLineItemById(productId, { updateUrl: false });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleOrderLineItemChange = (itemId: string) => {
		clearJawsTabSelection();
		setSearchTab('1');
		loadOrderLineItemById(itemId);
	};

	const handleSearchTabChange = (tabKey: string) => {
		setSearchTab(tabKey);
		if (tabKey === '1') {
			clearJawsTabSelection();
			updateUrlQueryParams({
				[TAB_QUERY_PARAM]: 'product',
				[CONFIGURATION_QUERY_PARAM]: null,
			});
		} else {
			clearProductSelection();
			updateUrlQueryParams({
				[TAB_QUERY_PARAM]: 'jaws',
				[PRODUCT_QUERY_PARAM]: null,
			});
		}
	};

	const handleDeviceChange = (value: string) => {
		setSelectedDeviceId(value);
	};
	const handleJawAssemblyChange = (value: string) => {
		const jaw = jawAssemblies.find((ja) => ja.id === value) ?? null;
		setSelectedJawAssembly(jaw);
		clearJawConfigurationSelection();
	};
	const handleStockShapeChange = (value: string) => {
		setSelectedStockShape(value);
		setSelectedStockWidth(null);
		setSelectedStockLength(null);
		setSelectedConfiguration(null);
		setOverlayImage(null);
		setConfigurationQueryParam(null);
	};
	const handleStockWidthChange = (value: number | string | undefined) => {
		setSelectedStockWidth(value ?? null);
		setSelectedStockLength(null);
		setSelectedConfiguration(null);
		setOverlayImage(null);
		setConfigurationQueryParam(null);
	};
	const handleStockLengthChange = (value: number | string | undefined) => {
		setSelectedStockLength(value ?? null);
		setSelectedConfiguration(null);
		setOverlayImage(null);
		setConfigurationQueryParam(null);
	};
	const handleConfigurationChange = (value: string) => {
		if (!selectedJawAssembly) return;

		const config = getFilteredConfigurations(
			selectedJawAssembly,
			getConfigurationFilters(
				selectedStockShape,
				selectedStockWidth,
				selectedStockLength
			)
		).find((conf) => conf.id === value);

		clearProductSelectionState();
		applyConfigurationSelection(config ?? null, {
			[PRODUCT_QUERY_PARAM]: null,
			[TAB_QUERY_PARAM]: 'jaws',
		});
	};

	const availableStockWidths = getAvailableStockWidths(
		selectedJawAssembly,
		selectedStockShape
	);
	const availableStockLengths = getAvailableStockLengths(
		selectedJawAssembly,
		selectedStockShape,
		selectedStockWidth
	);
	const filteredConfigurations = getFilteredConfigurations(
		selectedJawAssembly,
		getConfigurationFilters(
			selectedStockShape,
			selectedStockWidth,
			selectedStockLength
		)
	);

	const adjustOverlay = (direction: 'up' | 'down' | 'left' | 'right') => {
		const step = 0.125;
		if (direction === 'up') {
			setOverlayOffsetY((prev) => prev - step);
		} else if (direction === 'down') {
			setOverlayOffsetY((prev) => prev + step);
		} else if (direction === 'left') {
			setOverlayOffsetX((prev) => prev - step);
		} else if (direction === 'right') {
			setOverlayOffsetX((prev) => prev + step);
		}
	};

	const renderCamExports = (camExports: CamDataJawsRow[]) => {
		if (camExports.length === 0) {
			return <>No Jaws selected for this line item</>;
		}

		return (
			<Tabs
				defaultActiveKey="0"
				style={{ marginTop: '1rem' }}
				items={camExports.map((exportItem, index) => {
					const setup2SoftJaw = exportItem.setup2_soft_jaw;
					const setup2SoftJawOverlayDataUrl = normalizeBase64PngToDataUrl(
						setup2SoftJaw?.overlay_png_base64
					);
					const setup1Configuration =
						exportItem.setup_1_jaw_assembly_configuration;

					return {
						key: String(index),
						// Exports rarely name a machine; the model ("Haas VF-3SS") is
						// the label the station recognises anyway.
						label: exportItem.machine_model || `Export ${index + 1}`,
						children: (
							<>
								<Flex vertical={true}>
									<span>Number of Setups</span>
									<span>{exportItem.setup_count}</span>
								</Flex>
								<br />
								<Card title={setup1Configuration?.jaw_assembly?.name}>
									<Flex vertical={true}>
										<span>Jaw Configuration</span>
										<span>{setup1Configuration?.name}</span>
										<br />
										<Button
											type="primary"
											onClick={() => {
												applyConfigurationSelection(setup1Configuration);
											}}
										>
											Configure
										</Button>
									</Flex>
								</Card>

								{setup2SoftJaw ? (
									<>
										<br />
										<Card
											title={
												setup2SoftJaw?.setup2_component_name ||
												'Setup 2 Soft Jaw'
											}
										>
											<Flex vertical={true}>
												{setup2SoftJaw?.setup2_occurrence_name ? (
													<>
														<span>Occurrence</span>
														<span>{setup2SoftJaw?.setup2_occurrence_name}</span>
														<br />
													</>
												) : null}
												<Button
													type="primary"
													disabled={!setup2SoftJawOverlayDataUrl}
													onClick={() => {
														if (!setup2SoftJawOverlayDataUrl) {
															message.error(
																'No setup2 soft jaw overlay image found.'
															);
															return;
														}
														setOverlayImage(setup2SoftJawOverlayDataUrl);
													}}
												>
													Configure
												</Button>
											</Flex>
										</Card>
									</>
								) : null}
							</>
						),
					};
				})}
			/>
		);
	};

	const searchTabItems = [
		{
			key: '1',
			label: 'Search by Product',
			children: (
				<div style={{ padding: '1rem' }}>
					<Select
						showSearch
						placeholder="Products"
						onSearch={fetchOrders}
						onChange={handleOrderLineItemChange}
						value={pendingOrderLineItemId ?? selectedOrderLineItem?.id}
						filterOption={false}
						optionLabelProp="label"
						style={{ width: '100%', marginBottom: '0.5rem' }}
						disabled={isLoadingOrderLineItem}
						loading={isLoadingOrderLineItem}
					>
						{orders.map((order) => (
							<Option key={order.id} value={order.id} label={order.name}>
								<div
									style={{
										display: 'flex',
										alignItems: 'center',
										gap: '8px',
									}}
								>
									{order.thumbnail_file && (
										<img
											src={order.thumbnail_file}
											alt="thumb"
											width="48px"
											height="48px"
										/>
									)}
									{`${order.name} (${order.order?.name || 'Unknown Order'})`}
								</div>
							</Option>
						))}
					</Select>
					<Button
						block
						onClick={clearProductSelection}
						disabled={
							isLoadingOrderLineItem ||
							(!selectedOrderLineItem && !pendingOrderLineItemId)
						}
						style={{ marginBottom: '1rem' }}
					>
						Reset
					</Button>
					{isLoadingOrderLineItem && (
						<Flex
							align="center"
							gap={8}
							style={{ marginBottom: '1rem', color: '#bfbfbf' }}
						>
							<Spin size="small" />
							<span>Fetching order line item...</span>
						</Flex>
					)}
					{selectedOrderLineItem &&
						camJawExports &&
						!isLoadingOrderLineItem &&
						renderCamExports(camJawExports)}
				</div>
			),
		},
		{
			key: '2',
			label: 'Search by Jaws',
			children: (
				<div style={{ marginBottom: '20px' }}>
					<Select
						style={{ width: '100%', marginBottom: '10px' }}
						placeholder="Select Jaw Assembly"
						onChange={handleJawAssemblyChange}
						value={selectedJawAssembly?.id}
					>
						{jawAssemblies.map((ja) => (
							<Option key={ja.id} value={ja.id}>
								{ja.name}
							</Option>
						))}
					</Select>
					{selectedJawAssembly && selectedJawAssembly.configurations && (
						<Select
							style={{ width: '100%', marginBottom: '10px' }}
							placeholder="Select Stock Shape"
							onChange={handleStockShapeChange}
							value={selectedStockShape}
						>
							{Array.from(
								new Set(
									selectedJawAssembly.configurations.map(
										(conf) => conf.stock_shape
									)
								)
							).map((shape, index) => (
								<Option key={index} value={shape}>
									{shape}
								</Option>
							))}
						</Select>
					)}
					{selectedStockShape && !isRoundStockShape(selectedStockShape) && (
						<Flex gap={8} style={{ marginBottom: '10px' }}>
							<Select
								style={{ flex: 1 }}
								placeholder="Select Width"
								onChange={handleStockWidthChange}
								value={selectedStockWidth}
								allowClear
							>
								{availableStockWidths.map((width) => (
									<Option key={width} value={width}>
										{width}
									</Option>
								))}
							</Select>
							<Select
								style={{ flex: 1 }}
								placeholder="Select Min Length"
								onChange={handleStockLengthChange}
								value={selectedStockLength}
								disabled={selectedStockWidth == null}
								allowClear
							>
								{availableStockLengths.map((minLength) => (
									<Option key={minLength} value={minLength}>
										{minLength}
									</Option>
								))}
							</Select>
						</Flex>
					)}
					<Select
						style={{ width: '100%' }}
						placeholder="Select Configuration"
						onChange={handleConfigurationChange}
						disabled={!selectedJawAssembly}
						value={selectedConfiguration?.id}
					>
						{filteredConfigurations.map((config) => (
							<Option key={config.id} value={config.id}>
								{config.name || `Configuration ${config.id}`}
							</Option>
						))}
					</Select>
				</div>
			),
		},
	];

	return (
		<div className="flex flex-col w-full" style={{ padding: '0 24px' }}>
			<div
				className="flex justify-between items-center"
				style={{ marginTop: 20 }}
			>
				<Title level={2} style={{ margin: 0 }}>
					Jaw Configuration
				</Title>
				<Button
					type="text"
					style={{ fontSize: 24 }}
					onClick={() => navigate('/tablet/operations')}
				>
					<ArrowLeftOutlined />
					<span>Back</span>
				</Button>
			</div>

			<Splitter
				style={{
					height: 'calc(100vh - 130px)',
					backgroundColor: '#141414',
					padding: '1rem',
					marginTop: 12,
				}}
			>
				{/* Left Panel: Canvas with WebGL-rendered video */}
				<Splitter.Panel
					defaultSize={720}
					style={{
						position: 'relative',
						width: '100%',
						height: '100%',
						overflow: 'hidden',
					}}
				>
					<div
						ref={panelRef}
						style={{
							width: '100%',
							height: '100%',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
						}}
					>
						{/* Hidden video element as texture source */}
						<video
							ref={videoRef}
							autoPlay
							playsInline
							muted
							style={{
								position: 'absolute',
								width: '1px',
								height: '1px',
								opacity: 0,
							}}
						/>
						<div
							ref={viewportRef}
							style={{
								position: 'relative',
								flexShrink: 0,
								...(viewportLayout
									? {
											width: viewportLayout.width,
											height: viewportLayout.height,
									  }
									: {
											width: '100%',
											aspectRatio: videoAspectRatio,
											maxHeight: '100%',
									  }),
							}}
						>
							<canvas
								ref={canvasRef}
								style={{
									display: 'block',
									width: '100%',
									height: '100%',
									transform: 'scale(-1, 1)',
								}}
							/>
							{overlayImage && (
								<img
									src={overlayImage}
									alt="Overlay"
									style={{
										position: 'absolute',
										top: '50%',
										left: '50%',
										// Height-based so overlaySize=100 means "same height as
										// the video feed" — a portrait wireframe sized by width
										// overflows a 16:9 feed and starts far too tall.
										width: 'auto',
										height: '100%',
										transform: `translate(calc(-50% + ${overlayOffsetX}%), calc(-50% + ${overlayOffsetY}%)) scale(${
											overlaySize / 100
										})`,
										transformOrigin: '50% 50%',
										pointerEvents: 'none',
										opacity: overlayOpacity / 100,
									}}
								/>
							)}
						</div>
					</div>
				</Splitter.Panel>

				{/* Right Panel: Controls and Tabs */}
				<Splitter.Panel
					defaultSize={360}
					style={{
						padding: '1rem',
						backgroundColor: '#fff',
						overflow: 'auto',
					}}
				>
					{devices.length > 0 && (
						<div style={{ marginBottom: '20px' }}>
							<div style={{ marginBottom: '6px', fontWeight: 500 }}>Camera</div>
							<Select
								style={{ width: '100%' }}
								placeholder="Select Camera"
								onChange={handleDeviceChange}
								value={selectedDeviceId}
							>
								{devices.map((device) => (
									<Option key={device.deviceId} value={device.deviceId}>
										{device.label || `Camera ${device.deviceId}`}
									</Option>
								))}
							</Select>
						</div>
					)}
					<Tabs
						activeKey={searchTab}
						onChange={handleSearchTabChange}
						items={searchTabItems}
					/>
					<div style={{ marginTop: '1rem', marginBottom: '1rem' }}>
						<span>Overlay Opacity: {overlayOpacity}%</span>
						<Slider
							min={10}
							max={100}
							step={1}
							value={overlayOpacity}
							onChange={(value) => setOverlayOpacity(value)}
						/>
					</div>
					<Collapse
						style={{ marginTop: '1rem' }}
						items={[
							{
								key: 'calibration',
								label: 'Calibration',
								children: (
									<>
										<div style={{ marginBottom: '20px', textAlign: 'center' }}>
											<Button
												icon={<UpOutlined />}
												onClick={() => adjustOverlay('up')}
											/>
											<div
												style={{
													display: 'flex',
													justifyContent: 'center',
													margin: '10px 0',
												}}
											>
												<Button
													icon={<LeftOutlined />}
													onClick={() => adjustOverlay('left')}
												/>
												<Button
													icon={<DownOutlined />}
													onClick={() => adjustOverlay('down')}
													style={{ margin: '0 5px' }}
												/>
												<Button
													icon={<RightOutlined />}
													onClick={() => adjustOverlay('right')}
												/>
											</div>
											<div style={{ marginTop: '8px' }}>
												X: {overlayOffsetX.toFixed(3)}% &nbsp; Y:{' '}
												{overlayOffsetY.toFixed(3)}%
											</div>
										</div>
										<div
											style={{
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												marginBottom: '20px',
											}}
										>
											<Button
												icon={<MinusOutlined />}
												onClick={() => setOverlaySize((prev) => prev - 0.125)}
											/>
											<span style={{ margin: '0 10px' }}>{overlaySize}%</span>
											<Button
												icon={<PlusOutlined />}
												onClick={() => setOverlaySize((prev) => prev + 0.125)}
											/>
										</div>
										<div>
											<span>Distortion K1: {k1.toFixed(3)}</span>
											<Slider
												min={-1}
												max={1}
												step={0.01}
												value={k1}
												onChange={(value) => setK1(value)}
											/>
											<span>Distortion K2: {k2.toFixed(3)}</span>
											<Slider
												min={-1}
												max={1}
												step={0.01}
												value={k2}
												onChange={(value) => setK2(value)}
											/>
										</div>
									</>
								),
							},
						]}
					/>
				</Splitter.Panel>
			</Splitter>
		</div>
	);
};

export default JawConfiguration;
